/**
 * CR-8227: the level name inputs — what the customer sees for each level of
 * their organisation. Shows only the levels the structure uses (all three
 * while none is recorded).
 */
import type { OrganisationStructure } from '../../../lib/api';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/utils';
import { LEVEL_COLORS, LEVEL_NAME_MAX, levelsFor, type LevelNames } from './organisationSetup';

interface OrganisationLevelNamesProps {
  structure: OrganisationStructure;
  labels: LevelNames;
  onChange: (labels: LevelNames) => void;
  disabled?: boolean;
}

/**
 * @param structure - decides which levels are shown
 * @param labels - the current names
 * @param onChange - receives the whole set with one name changed
 * @param disabled - read only while a save is running
 */
export function OrganisationLevelNames({ structure, labels, onChange, disabled }: OrganisationLevelNamesProps) {
  const levels = levelsFor(structure);
  const fields: Array<{ key: keyof LevelNames; title: string; color: string; shown: boolean }> = [
    { key: 'group', title: 'Top level', color: LEVEL_COLORS.group.fill, shown: levels.group },
    { key: 'division', title: 'Middle level', color: LEVEL_COLORS.division.fill, shown: levels.division },
    { key: 'site', title: 'Site level', color: LEVEL_COLORS.site.fill, shown: true },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {fields.filter((field) => field.shown).map((field) => (
        <div key={field.key} className="space-y-1.5">
          <Label htmlFor={`level-name-${field.key}`} className="flex items-center gap-1.5 font-normal">
            <span className={cn('h-2.5 w-2.5 rounded-full', field.color)} aria-hidden="true" />
            {field.title}
          </Label>
          <Input
            id={`level-name-${field.key}`}
            value={labels[field.key]}
            maxLength={LEVEL_NAME_MAX}
            disabled={disabled}
            onChange={(e) => onChange({ ...labels, [field.key]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}
